import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../shared/api/axios';
import SmartVideoPlayer from './SmartVideoPlayer';
import { useMediaViewerOverlay, viewerItemsFromMixed } from './MediaViewerOverlay';
import { useTranslation } from 'react-i18next';

// ---------------- Types ----------------
type MediaEntry =
  | { kind: 'image'; url: string }
  | {
      kind: 'video';
      url?: string;
      videoId?: string | null;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
    };
     
type Chunk = { kind: 'portrait' | 'landscape', indices: number[] };

interface MediaPost {
  _id: string;
  media: MediaEntry[];
  title?: string;
}


// ---------------- URL helpers ----------------
const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';
const VIDEO_CDN = process.env.REACT_APP_PUBLIC_VIDEO_BASE_URL || '';

const toImageUrl = (p: any): string => {
  const s = String(p || '').trim();
  if (!s) return '';
  if (/^(https?:|data:)/i.test(s)) return s;
  return `${IMAGE_CDN.replace(/\/$/, '')}/${s.replace(/^\/+/, '')}`;
};

const toVideoUrl = (p?: string | null): string => {
  const s = String(p || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `${VIDEO_CDN.replace(/\/$/, '')}/${s.replace(/^\/+/, '')}`;
};

const resolveVideoUrl = (m: Extract<MediaEntry, { kind: 'video' }>) => {
  if (m.hlsUrl) return toVideoUrl(m.hlsUrl);
  if (m.url) return toVideoUrl(m.url);
  if (m.videoId) {
    return `${VIDEO_CDN.replace(/\/$/, '')}/media/videos/${m.videoId}/hls/index.m3u8`;
  }
  return '';
};


// Build label text consistently
const mediaLabelOf = (m: MediaEntry): string => {
  if (m.kind !== 'video') return '';
  const t = (m.musicTitle || '').trim();
  const a = (m.musicArtist || '').trim();
  if (!t && !a) return '';
  return t && a ? `${t} — ${a}` : (t || a);
};


// Normalize API payload to strings/objects with music fields preserved
const coerceMediaArray = (raw: any): any[] => {
  const candidates = [raw?.media, raw?.mediaUrls, raw?.medias, raw?.attachments, raw?.files, raw?.gallery, raw?.items];
  const first = candidates.find(v => v != null);
  if (Array.isArray(first)) return first;
  if (first) return [first];
  return [];
};



const normalizeMediaToEntries = (raw: any): MediaEntry[] =>
  coerceMediaArray(raw)
    .map((item) => {
      if (typeof item === 'string') {
        return { kind: 'image', url: toImageUrl(item) };
      }

      if (!item || typeof item !== 'object') return null;

      const isVideoLike =
        item.kind === 'video' ||
        item.type === 'video' ||
        item.isHls ||
        !!item.videoId ||
        !!item.hlsUrl ||
        !!item.dashUrl ||
        /\.m3u8($|\?)/i.test(item.url || '') ||
        /\.mpd($|\?)/i.test(item.url || '');

      if (isVideoLike) {
        return {
          kind: 'video',
          url: item.url || undefined,
          videoId: item.videoId ?? null,
          hlsUrl: item.hlsUrl ?? item.url ?? null,
          dashUrl: item.dashUrl ?? null,
          posterUrl: item.posterUrl ?? null,
          musicTitle: item.musicTitle ?? item.music?.title ?? null,
          musicArtist: item.musicArtist ?? item.music?.artist ?? null,
        };
      }

      const img =
        item.url ??
        item.image?.url ??
        item.src ??
        item.path ??
        null;

      if (!img) return null;

      return { kind: 'image', url: toImageUrl(img) };
    })
    .filter(Boolean) as MediaEntry[];

    
// ---------------- Component ----------------
const MediaPresentationMode = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<MediaPost | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // root for minor utilities (kept)
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Hover state for showing music captions in presentation UI
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);
  const handleHoverEnter = (key: string) => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredKey(key);
  };
  const handleHoverLeave = () => {
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredKey(null);
      hoverTimeoutRef.current = null;
    }, 500);
  };
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  // ✅ Shared overlay
  const { openViewer: openOverlay, Overlay, setMuted } =
    useMediaViewerOverlay({ initialMuted: true, pauseBackgroundOnOpen: true });

  // Ensure overlay starts muted (or sync to a globalMuted if you have one)
  useEffect(() => { setMuted(true); }, [setMuted]);

  // Fetch data (preserve music metadata)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/media/${id}`);
        const raw = res.data;
        const media = normalizeMediaToEntries(raw);

        setPost({
          _id: String(raw?._id ?? id),
          title: raw?.title ?? '',
          media,
        });
      } catch (err) {
        console.error('Failed to load media post:', err);
      }
    })();
  }, [id]);


  //Landscape media

  // Track which videos are landscape
const [isVideoLandscape, setIsVideoLandscape] = useState<Record<number, boolean>>({});

// Add near other state
const [imgDims, setImgDims] = useState<Record<number, {w:number; h:number}>>({});
const [vidDims, setVidDims] = useState<Record<number, {w:number; h:number}>>({});

// Update handlers
const handleImgLoad = (index: number, ev: React.SyntheticEvent<HTMLImageElement>) => {
  const img = ev.currentTarget;
  const w = img.naturalWidth || 0;
  const h = img.naturalHeight || 0;
  const landscape = w >= h;
  setIsLandscape(prev => (prev[index] === landscape ? prev : { ...prev, [index]: landscape }));
  if (w && h) setImgDims(prev => ({ ...prev, [index]: { w, h } }));
};

const handleVideoMeta = (index: number) => (ev: React.SyntheticEvent<HTMLVideoElement>) => {
  const v = ev.currentTarget;
  const w = v.videoWidth || 0;
  const h = v.videoHeight || 0;
  const landscape = w >= h;
  setIsVideoLandscape(prev => (prev[index] === landscape ? prev : { ...prev, [index]: landscape }));
  if (w && h) setVidDims(prev => ({ ...prev, [index]: { w, h } }));
};


  // Track which grid images are landscape
const [isLandscape, setIsLandscape] = useState<Record<number, boolean>>({});


const isLandscapeNow = (index: number, isVideo: boolean) =>
  isLandscape[index] || (isVideo && isVideoLandscape[index]);


  // 👉 open overlay at a specific index, preserving music info
  const openPostViewer = (index: number) => {
    if (!post) return;
    const items = viewerItemsFromMixed(post.media, (m: any) =>
  m.kind === 'video' ? resolveVideoUrl(m) : m.url
);
    openOverlay(items, index);
  };

  if (!post) return <div style={styles.loading}>{t('mediaPresentation.loading')}</div>;
  const isSingle = (post?.media.length ?? 0) === 1;
  const isTwo = (post?.media.length ?? 0) === 2;

  const chunks: Chunk[] = [];
if (post) {
  let cur: Chunk | null = null;
  post.media.forEach((m, index) => {
  const isVid = m.kind === 'video';
  const land = isLandscapeNow(index, isVid);
  const kind: Chunk['kind'] = land ? 'landscape' : 'portrait';

  if (!cur || cur.kind !== kind) {
    cur = { kind, indices: [index] };
    chunks.push(cur);
  } else {
    cur.indices.push(index);
  }
});

}
  return (
    <div
      ref={rootRef}
      className={isSingle ? 'no-scrollbars' : undefined}
      style={{
        ...styles.container,
        ...(isSingle ? styles.noScrollContainer : styles.noHScrollContainer),
      }}
      onWheel={isSingle ? (e) => e.preventDefault() : undefined}
      onTouchMove={isSingle ? (e) => e.preventDefault() : undefined}
    >
      <button onClick={() => navigate(-1)} style={styles.exitButton}>✖</button>

      {isSingle ? (
        // Single centered
        <div style={styles.singleWrap}>
          {(() => {
            const m = post.media[0];
            const isVideo = m.kind === 'video';
            const url = isVideo ? resolveVideoUrl(m) : m.url;
            const label = mediaLabelOf(m);

            return isVideo ? (
              <div
                style={{ position: 'relative', display: 'inline-block' }}
                onMouseEnter={() => handleHoverEnter('single')}
                onMouseLeave={handleHoverLeave}
              >
                <SmartVideoPlayer
                  videoUrl={url}
                  controls
                  muted
                  loop
                  preload="auto"
                  autoPlayWhenVisible={true}
                  noVisibilityPause
                  videoProps={{ autoPlay: true, 'data-grid-video': '1' }}
                  containerStyle={{ borderRadius: 12 }}
                  videoStyle={{
                    borderRadius: 12,
                    objectFit: 'contain',
                    height: '100%',
                    width: 'auto',
                    maxWidth: '800px',
                  }}
                />
                {!!label && (
                  <div
                    title={label}
                    style={{
                      position: 'absolute',
                      left: 0, right: 0, bottom: -13,
                      padding: '6px 10px 10px',
                      fontSize: 13,
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 100%)',
                      pointerEvents: 'none',
                      opacity: hoveredKey === 'single' ? 1 : 0,
                      transition: 'opacity 220ms ease',
                    }}
                  >
                    <span aria-hidden="true" style={{ flex: '0 0 auto' }}>🎵</span>
                    <span style={{ minWidth: 0, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              // Click image to open overlay (single)
              <img
                src={url}
                alt="Single media"
                style={styles.singleMedia}
                onClick={() => openPostViewer(0)}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            );
          })()}
        </div>

) : isTwo ? (
  // ✅ Two-up, side-by-side, same item policy as multi
  <div style={styles.twoUpGrid}>
    {[0,1].map((index) => {
      const m = post.media[index];
      const url = m.kind === 'video' ? resolveVideoUrl(m) : m.url;
      const isVid = m.kind === 'video';

      const label = mediaLabelOf(m);
      const hk = `m-${index}`;
      const land = isLandscapeNow(index, isVid);

      // aspect ratio style for landscape tiles (same as your landscape run)
      const dims = isVid ? vidDims[index] : imgDims[index];
      const arStyle: React.CSSProperties =
        land ? (dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : { aspectRatio: '16 / 9' }) : {};

      return (
        <div
          key={`${url}-${index}`}
          style={{
            ...styles.mediaBox,
            ...styles.twoUpCell, 
            ...arStyle,
            position: 'relative',
          }}
          onMouseEnter={() => handleHoverEnter(hk)}
          onMouseLeave={handleHoverLeave}
          onClick={() => !isVid && openPostViewer(index)}
        >
          {isVid ? (
            land ? (
              // landscape video → fill aspect-ratio tile
              <>
                <SmartVideoPlayer
                  videoUrl={url}
                  controls
                  muted
                  loop
                  preload="metadata"
                  autoPlayWhenVisible={true}
                  videoProps={{ autoPlay: true, 'data-grid-video': '1', onLoadedMetadata: handleVideoMeta(index) }}
                  visibilityRootMargin="800px 0px"
                  containerStyle={{ borderRadius: 12, objectFit: 'contain', width: 'auto', height: 'auto' }}
                  
                />
                {!!label && (
                  <div title={label} style={{ ...styles.caption, opacity: hoveredKey === hk ? 1 : 0 }} aria-live="polite">
                    <span aria-hidden="true" style={{ flex: '0 0 auto' }}>🎵</span>
                    <span style={styles.captionText}>{label}</span>
                  </div>
                )}
              </>
            ) : (
              // portrait/unknown video → same as multi (contain)
              <>
                <SmartVideoPlayer
                  videoUrl={url}
                  controls
                  muted
                  loop
                  preload="metadata"
                  autoPlayWhenVisible={true}
                  videoProps={{ autoPlay: true, 'data-grid-video': '1', onLoadedMetadata: handleVideoMeta(index) }}
                  visibilityRootMargin="800px 0px"
                  containerStyle={{
                    borderRadius: 12, 
                    
                  }}
                  
                  videoStyle={{ borderRadius: 12,  }}
                />
                {!!label && (
                  <div title={label} style={{ ...styles.caption, opacity: hoveredKey === hk ? 1 : 0 }} aria-live="polite">
                    <span aria-hidden="true" style={{ flex: '0 0 auto' }}>🎵</span>
                    <span style={styles.captionText}>{label}</span>
                  </div>
                )}
              </>
            )
          ) : land ? (
            // landscape image → fill aspect-ratio tile
            <img
              src={url}
              alt={`media-${index}`}
              onLoad={(e) => handleImgLoad(index, e)}
              style={styles.landMediaFill}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            // portrait/unknown image → same as multi (natural)
            <img
              src={url}
              alt={`media-${index}`}
              onLoad={(e) => handleImgLoad(index, e)}
              style={styles.twoUpPortraitContain}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
      );
    })}
  </div>
      ) : (
        // Multi grid
        <div style={styles.gridOuter}>
          <div style={styles.grid}>
          {chunks.map((chunk, ci) => {
  if (chunk.kind === 'portrait') {
    // Masonry for non-landscape (your existing column layout)
    return (
      <div key={`p-${ci}`} style={styles.columns}>
        {chunk.indices.map((index) => {
          const m = post.media[index];
          const url = m.kind === 'video' ? resolveVideoUrl(m) : m.url;
          const isVid = m.kind === 'video';
          const label = mediaLabelOf(m);
          const hk = `m-${index}`;

          return (
            <div
              key={`${url}-${index}`}
              style={{ ...styles.mediaBox, position: 'relative' }}
              onMouseEnter={() => handleHoverEnter(hk)}
              onMouseLeave={handleHoverLeave}
            >
              {isVid ? (
                <>
                  <SmartVideoPlayer
                    videoUrl={url}
                    controls
                    muted
                    loop
                    preload="metadata"
                    autoPlayWhenVisible={true}
                    suppressPauseNearEndSec={2}
                    pauseDebounceMs={350}
                    visibilityRootMargin="800px 0px"
                    videoProps={{
                      autoPlay: true,
                      'data-grid-video': '1',
                      onLoadedMetadata: handleVideoMeta(index),
                    }}
                    containerStyle={{ borderRadius: 12 }}
                    videoStyle={{ borderRadius: 12, objectFit: 'contain', width: '90%', height: 'auto' }}
                  />
                  {!!label && (
                    <div
                    title={label}
                    style={{ ...styles.caption, opacity: hoveredKey === hk ? 1 : 0 }}
                    aria-live="polite"
                  >
                    <span aria-hidden="true" style={{ flex: '0 0 auto' }}>🎵</span>
                    <span style={styles.captionText}>{label}</span>
                  </div>
                  )}
                </>
              ) : (
                <img
                  src={url}
                  alt={`media-${index}`}
                  onLoad={(e) => handleImgLoad(index, e)}
                  style={styles.thumb}
                  onClick={() => openPostViewer(index)}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Landscape run → its own Grid row
  return (
    <div key={`l-${ci}`} style={styles.landRowGrid}>
      {chunk.indices.map((index) => {
        const m = post.media[index];
        const url = m.kind === 'video' ? resolveVideoUrl(m) : m.url;
        const isVid = m.kind === 'video';
        const label = mediaLabelOf(m);
        const hk = `m-${index}`;
        const dims = isVid ? vidDims[index] : imgDims[index];
        const arStyle: React.CSSProperties =
          dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : { aspectRatio: '16 / 9' }; // sensible fallback
        
        return (
          <div
  key={`${url}-${index}`}
  style={{ ...styles.mediaBox, ...arStyle, position: 'relative' }}
  onMouseEnter={() => handleHoverEnter(hk)}
  onMouseLeave={handleHoverLeave}
>
            {isVid ? (
              <>
                <SmartVideoPlayer
                  videoUrl={url}
                  controls
                  muted
                  loop
                  preload="metadata"
                  autoPlayWhenVisible={true}
                  suppressPauseNearEndSec={2}
                  pauseDebounceMs={350}
                  visibilityRootMargin="800px 0px"
                  videoProps={{
                    autoPlay: true,
                    'data-grid-video': '1',
                    onLoadedMetadata: handleVideoMeta(index),
                  }}
                  containerStyle={{ borderRadius: 12, width: '100%', height: '100%' }}
                  videoStyle={styles.landMediaFill}
                  />
                {!!label && (
                  <div
                  title={label}
                  style={{ ...styles.caption, opacity: hoveredKey === hk ? 1 : 0 }}
                  aria-live="polite"
                >
                  <span aria-hidden="true" style={{ flex: '0 0 auto' }}>🎵</span>
                  <span style={styles.captionText}>{label}</span>
                </div>
                )}
              </>
            ) : (
              <img
                src={url}
                alt={`media-${index}`}
                onLoad={(e) => handleImgLoad(index, e)}
                style={styles.landMediaFill}
                onClick={() => openPostViewer(index)}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
})}


            {post.media.length === 0 && (
              <div style={{ color: '#888', padding: '16px 0' }}>{t('mediaPresentation.empty')}</div>
            )}
          </div>
        </div>
      )}

      {/* Shared overlay lives once per page; it will show the same music label since we pass objects */}
      {Overlay}
    </div>
  );
};

// ---------------- Styles ----------------

const LANDSCAPE_TILE_HEIGHT = 'clamp(260px, 30vw, 420px)';

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#000',
    minHeight: '100vh',
    padding: '30px',
    boxSizing: 'border-box',
    position: 'relative',
    color: '#fff',
  },
  noHScrollContainer: {
    overflowX: 'hidden',
    overflowY: 'auto',
    touchAction: 'pan-y',
  },
  noScrollContainer: {
    height: '100vh',
    overflow: 'hidden',
    overscrollBehavior: 'none',
    touchAction: 'none',
  },
  gridOuter: {
    width: '100%',
    overflowX: 'hidden',
    contain: 'content',
  },
  grid: { paddingTop: '2%' }, // no columns here!
  mediaBox: {
    display: 'inline-block',
    width: '100%',
    marginBottom: '16px',
    breakInside: 'avoid',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
    marginTop: '2%',
  },
  
  thumb: {
    width: '100%',
    height: 'auto', // safer for non-landscape
    display: 'block',
    borderRadius: '12px',
  },
  exitButton: {
    position: 'absolute',
    top: 15,
    right: 26,
    background: 'transparent',
    color: '#ccc',
    border: 'none',
    fontSize: '20px',
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '50%',
    zIndex: 10000,
  },
  singleWrap: {
    minHeight: 'calc(100vh - 80px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  singleMedia: {
    maxWidth: '92vw',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: 12,
    display: 'block',
    boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
    cursor: 'zoom-in',
  },
  loading: {
    color: '#bbb',
    padding: 24,
  },

  

  tileCover: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
    borderRadius: 12,
  },

  landscapeThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    borderRadius: '12px',
  },

  columns: { columnCount: 3, columnGap: '16px', paddingTop: '2%' },


  // Landscape runs → proper grid that can be wide
  landRowGrid: {
    display: 'grid',
    gridAutoFlow: 'row',
    gap: 16,
    paddingTop: '2%',
    // each item is wide; tune minmax to control how many per row
    gridTemplateColumns: 'repeat(auto-fill, minmax(520px, 1fr))', // a bit wider than before
  },

  captionText: {
    minWidth: 0,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },

  caption: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -13,               // keep your offset
    padding: '6px 10px 10px',
    fontSize: 13,
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    background:
      'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 100%)',
    pointerEvents: 'none',
    transition: 'opacity 220ms ease',
    opacity: 0,                // default hidden; we’ll override inline
  },

  landMediaFill: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
    borderRadius: 12,
  },

  
  twoUpGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    // same vertical footprint as your multi container -> no scroll when 2 items
    height: 'calc(100vh - 60px)', // matches container padding (30px top/bottom)
    alignItems: 'center',
    overflow: 'hidden',
    paddingTop: '2%',
  },
  
  // For portrait images in two-up: keep aspect, use max size that fits the half
  twoUpPortraitContain: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    borderRadius: 12,
  },

  twoUpCell: {
    display: 'block',   // override mediaBox's inline-block for the 2-up grid
    marginBottom: 0,
  }
};


export default MediaPresentationMode;
